import React, {useEffect, useState, useMemo} from 'react';
import { Link } from 'react-router-dom';
import {
    getUserById,
    getFarmerInventory,
    getFarmsByFarmerId,
    getSchemeApplicationsByFarmerId,
    getInputDeliveriesByFarmerId,
    getHarvestByFarmerId,
    getFarmerInputHistory,
    getRecentActivity
} from "../../api/web";
import { capitalize } from "../../utils/capitalize";
import moment from "moment";

const UserDetail = (props) => {

    const id = props.match.params.id;
    const userId = parseInt(id, 10);
    const [initialLoading, setInitialLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [inventory, setInventory] = useState([]);
    const [farms, setFarms] = useState([]);
    const [schemeApplications, setSchemeApplications] = useState([]);
    const [inputDeliveries, setInputDeliveries] = useState([]);
    const [harvests, setHarvests] = useState([]);
    const [inventoryHistory, setInventoryHistory] = useState([]);
    const [activityLog, setActivityLog] = useState([]);
    const [farmerDataLoading, setFarmerDataLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [activityLoading, setActivityLoading] = useState(false);

    const isFarmer = useMemo(() => {
        if (!user) return false;
        const jt = (user.job_title || '').toLowerCase();
        const jt2 = (user.job_title_secondary || '').toLowerCase();
        return jt === 'farmer' || jt2 === 'farmer';
    }, [user]);

    useEffect(() => {
        getUserById(id).then((response) => {
            if (!response.error) {
                setUser(response.payload);
            }
            setInitialLoading(false);
        });
    }, [id]);

    useEffect(() => {
        if (!user || !isFarmer || !userId) return;
        setFarmerDataLoading(true);
        Promise.all([
            getFarmerInventory(userId),
            getFarmsByFarmerId(userId),
            getSchemeApplicationsByFarmerId(userId),
            getInputDeliveriesByFarmerId(userId),
            getHarvestByFarmerId(userId)
        ]).then(([inv, f, apps, deliv, harv]) => {
            setInventory(Array.isArray(inv.payload) ? inv.payload : []);
            setFarms(Array.isArray(f.payload) ? f.payload : []);
            setSchemeApplications(Array.isArray(apps.payload) ? apps.payload : []);
            setInputDeliveries(Array.isArray(deliv.payload) ? deliv.payload : []);
            setHarvests(Array.isArray(harv.payload) ? harv.payload : []);
            setFarmerDataLoading(false);
        });
    }, [user, isFarmer, userId]);

    useEffect(() => {
        if (!user || !isFarmer) return;
        setHistoryLoading(true);
        getFarmerInputHistory().then((res) => {
            const list = Array.isArray(res.payload) ? res.payload : [];
            const forUser = list.filter((item) => {
                const fid = item.farmer?.id ?? item.farmer_id ?? item.farmer;
                return fid === user.id;
            });
            setInventoryHistory(forUser);
            setHistoryLoading(false);
        });
    }, [user, isFarmer]);

    useEffect(() => {
        setActivityLoading(true);
        getRecentActivity().then((res) => {
            const list = Array.isArray(res.payload) ? res.payload : [];
            const forUser = list.filter((item) => {
                const actorId = item.actor?.id ?? item.actor_id ?? item.actor;
                return actorId === userId;
            });
            setActivityLog(forUser);
            setActivityLoading(false);
        });
    }, [userId]);

    const roleDisplay = () => {
        if (!user) return '—';
        const primary = user.job_title === 'admin'
            ? (user.is_superuser ? 'Super Admin' : 'Admin')
            : (user.job_title ? capitalize(user.job_title) : '');
        const secondary = user.job_title_secondary ? capitalize(user.job_title_secondary) : '';
        if (primary && secondary) return `${primary}, ${secondary}`;
        return primary || secondary || '—';
    };

    const companyDisplay = (c) => {
        if (!c) return '—';
        if (typeof c === 'object') return c.name ?? c.pacra_id ?? c.id ?? '—';
        return String(c);
    };

    const applicationStatusBadge = (status) => {
        const map = {
            pending: { cls: 'text-white', style: { backgroundColor: '#fd7e14' } },
            under_review: { cls: 'bg-info text-white', style: {} },
            approved: { cls: 'bg-success text-white', style: {} },
            rejected: { cls: 'bg-danger text-white', style: {} }
        };
        const c = map[(status || '').toLowerCase()] || { cls: 'bg-secondary text-white', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{status || '—'}</span>;
    };

    if (initialLoading) {
        return (
            <div className="content-body">
                <div className="container d-flex justify-content-center ht-100p">
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p className="mg-t-10">Loading user</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="content-body">
                <div className="container pd-x-0">
                    <div className="mg-b-20">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Not found</li>
                            </ol>
                        </nav>
                        <p className="tx-danger">User not found.</p>
                        <Link to="/user" className="btn btn-sm btn-primary">Back to Users</Link>
                    </div>
                </div>
            </div>
        );
    }

    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || '—';

    return (
        <div className="content-body">
            <div className="container pd-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">{fullName}</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-5 tx-spacing--1">{fullName}</h4>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <span className="tx-color-03 tx-13">@{user.username}</span>
                            <span className="tx-color-03 tx-13">·</span>
                            <span className="tx-color-03 tx-13">{roleDisplay()}</span>
                        </div>
                    </div>
                    <div className="d-none d-md-block">
                        <Link to="/user" className="btn btn-sm pd-x-15 btn-secondary btn-uppercase">
                            <i className="wd-10 mg-r-5 fa fa-arrow-left"></i> Back to list
                        </Link>
                    </div>
                </div>

                <div className="row row-xs">
                    <div className="col-md-12 mg-t-10">
                        <div className="nav-wrapper mg-b-20 tx-13">
                            <ul className="nav nav-line nav-fill tx-medium">
                                <li className="nav-item">
                                    <a href="#user_details" className="nav-link active" data-bs-toggle="tab">
                                        <i className="ion-md-person"/> User Details
                                    </a>
                                </li>
                                {isFarmer && (
                                    <>
                                        <li className="nav-item">
                                            <a href="#inventory_farms" className="nav-link" data-bs-toggle="tab">
                                                <i className="ion-md-cube"/> Inventory &amp; Farms ({inventory.length} / {farms.length})
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a href="#scheme_applications" className="nav-link" data-bs-toggle="tab">
                                                <i className="ion-md-document"/> Scheme Applications ({schemeApplications.length})
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a href="#input_deliveries" className="nav-link" data-bs-toggle="tab">
                                                <i className="ion-md-truck"/> Input Deliveries ({inputDeliveries.length})
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a href="#harvest" className="nav-link" data-bs-toggle="tab">
                                                <i className="ion-md-leaf"/> Harvest ({harvests.length})
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a href="#inventory_history" className="nav-link" data-bs-toggle="tab">
                                                <i className="ion-md-time"/> Input History ({inventoryHistory.length})
                                            </a>
                                        </li>
                                    </>
                                )}
                                <li className="nav-item">
                                    <a href="#activity" className="nav-link" data-bs-toggle="tab">
                                        <i className="ion-md-pulse"/> Activity ({activityLog.length})
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div className="tab-content">
                            <div id="user_details" className="tab-pane fade active show">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">User Details</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row row-sm">
                                            <div className="col-md-6">
                                                <p><strong>Username:</strong> {user.username ?? '—'}</p>
                                                <p><strong>First Name:</strong> {user.first_name ?? '—'}</p>
                                                <p><strong>Last Name:</strong> {user.last_name ?? '—'}</p>
                                                <p><strong>Email:</strong> {user.email ?? '—'}</p>
                                                <p><strong>Phone:</strong> {user.phone_number ?? user.mobile_number ?? '—'}</p>
                                                <p><strong>Role:</strong> {roleDisplay()}</p>
                                                <p><strong>NRC:</strong> {user.nrc_number ?? user.nrc ?? '—'}</p>
                                            </div>
                                            <div className="col-md-6">
                                                <p><strong>Company:</strong> {companyDisplay(user.company)}</p>
                                                <p><strong>Town:</strong> {user.town?.name ?? user.town ?? '—'}</p>
                                                <p><strong>Province:</strong> {user.province?.name ?? user.province ?? '—'}</p>
                                                <p><strong>Date Joined:</strong> {(user.date_joined || user.date_added) ? moment(user.date_joined || user.date_added).format("Do MMM, YYYY HH:mm") : '—'}</p>
                                                <p><strong>Last Login:</strong> {user.last_login ? moment(user.last_login).format("Do MMM, YYYY HH:mm") : '—'}</p>
                                                <p><strong>Active:</strong> {user.is_active ? 'Yes' : 'No'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {isFarmer && (
                                <>
                                    <div id="inventory_farms" className="tab-pane fade">
                                        <div className="card mg-b-20">
                                            <div className="card-header">
                                                <h6 className="mg-b-0">Input Inventory</h6>
                                            </div>
                                            <div className="card-body">
                                                {farmerDataLoading ? (
                                                    <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                                        <span className="mg-l-10 tx-13">Loading...</span>
                                                    </div>
                                                ) : inventory.length === 0 ? (
                                                    <p className="tx-color-03 mg-b-0">No inventory recorded.</p>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Input</th>
                                                                    <th>Quantity</th>
                                                                    <th>Unit</th>
                                                                    <th>Last Updated</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {inventory.map((item, idx) => (
                                                                    <tr key={idx}>
                                                                        <td>{item.input ?? '—'}</td>
                                                                        <td>{item.quantity != null ? Number(item.quantity).toLocaleString() : '—'}</td>
                                                                        <td>{item.unit ?? '—'}</td>
                                                                        <td>{item.last_updated ? moment(item.last_updated).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="card">
                                            <div className="card-header">
                                                <h6 className="mg-b-0">Farms</h6>
                                            </div>
                                            <div className="card-body">
                                                {farmerDataLoading ? (
                                                    <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                                        <span className="mg-l-10 tx-13">Loading...</span>
                                                    </div>
                                                ) : farms.length === 0 ? (
                                                    <p className="tx-color-03 mg-b-0">No farms registered.</p>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Farm</th>
                                                                    <th>Province</th>
                                                                    <th>Town / Area</th>
                                                                    <th>Area (ha)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {farms.map((farm) => (
                                                                    <tr key={farm.id}>
                                                                        <td>
                                                                            <Link to={`/farm/${farm.id}`}>{farm.name ?? `Farm #${farm.id}`}</Link>
                                                                        </td>
                                                                        <td>{farm.province?.name ?? farm.province ?? '—'}</td>
                                                                        <td>{farm.nearest_town?.name ?? farm.town ?? farm.nearest_town ?? '—'}</td>
                                                                        <td>{farm.area_hectare ?? farm.standard_farmer_area ?? '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div id="scheme_applications" className="tab-pane fade">
                                        <div className="card">
                                            <div className="card-header">
                                                <h6 className="mg-b-0">Scheme Applications</h6>
                                            </div>
                                            <div className="card-body">
                                                {farmerDataLoading ? (
                                                    <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                                        <span className="mg-l-10 tx-13">Loading...</span>
                                                    </div>
                                                ) : schemeApplications.length === 0 ? (
                                                    <p className="tx-color-03 mg-b-0">No scheme applications.</p>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Scheme</th>
                                                                    <th>Status</th>
                                                                    <th>Applied</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {schemeApplications.map((app) => (
                                                                    <tr key={app.id}>
                                                                        <td>{app.scheme?.scheme_name ?? app.scheme_name ?? app.scheme ?? '—'}</td>
                                                                        <td>{applicationStatusBadge(app.app_status ?? app.status)}</td>
                                                                        <td>{app.date_created ? moment(app.date_created).format("Do MMM, YYYY") : '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div id="input_deliveries" className="tab-pane fade">
                                        <div className="card">
                                            <div className="card-header">
                                                <h6 className="mg-b-0">Input Deliveries</h6>
                                            </div>
                                            <div className="card-body">
                                                {farmerDataLoading ? (
                                                    <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                                        <span className="mg-l-10 tx-13">Loading...</span>
                                                    </div>
                                                ) : inputDeliveries.length === 0 ? (
                                                    <p className="tx-color-03 mg-b-0">No input deliveries.</p>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Scheme</th>
                                                                    <th>Quantity</th>
                                                                    <th>Farmer Confirmed</th>
                                                                    <th>Date</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {inputDeliveries.map((d) => (
                                                                    <tr key={d.id}>
                                                                        <td>{d.scheme?.scheme_name ?? d.scheme ?? '—'}</td>
                                                                        <td>{d.quantity != null ? d.quantity : '—'}</td>
                                                                        <td>{d.is_farmer_confirmed ? 'Yes' : 'No'}</td>
                                                                        <td>{d.date_created ? moment(d.date_created).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div id="harvest" className="tab-pane fade">
                                        <div className="card">
                                            <div className="card-header">
                                                <h6 className="mg-b-0">Harvest</h6>
                                            </div>
                                            <div className="card-body">
                                                {farmerDataLoading ? (
                                                    <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                                        <span className="mg-l-10 tx-13">Loading...</span>
                                                    </div>
                                                ) : harvests.length === 0 ? (
                                                    <p className="tx-color-03 mg-b-0">No harvest records.</p>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Scheme</th>
                                                                    <th>Tonnage</th>
                                                                    <th>Grade</th>
                                                                    <th>Status</th>
                                                                    <th>Date</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {harvests.map((h) => (
                                                                    <tr key={h.id}>
                                                                        <td>{h.scheme?.scheme_name ?? h.scheme ?? '—'}</td>
                                                                        <td>{h.tonnage != null ? Number(h.tonnage).toLocaleString() : '—'}</td>
                                                                        <td>{h.grade ?? '—'}</td>
                                                                        <td>{h.status ?? '—'}</td>
                                                                        <td>{h.date_created ? moment(h.date_created).format("Do MMM, YYYY") : '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div id="inventory_history" className="tab-pane fade">
                                        <div className="card">
                                            <div className="card-header">
                                                <h6 className="mg-b-0">Input Inventory History</h6>
                                            </div>
                                            <div className="card-body">
                                                {historyLoading ? (
                                                    <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                                        <span className="mg-l-10 tx-13">Loading...</span>
                                                    </div>
                                                ) : inventoryHistory.length === 0 ? (
                                                    <p className="tx-color-03 mg-b-0">No inventory history.</p>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Input</th>
                                                                    <th>Quantity</th>
                                                                    <th>Action</th>
                                                                    <th>Scheme</th>
                                                                    <th>Date</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {inventoryHistory.map((item) => (
                                                                    <tr key={item.id}>
                                                                        <td>{item.input_item?.name ?? item.input_item ?? '—'}</td>
                                                                        <td>{item.quantity != null ? item.quantity : '—'}</td>
                                                                        <td>{item.action_type ?? '—'}</td>
                                                                        <td>{item.scheme?.scheme_name ?? item.scheme ?? '—'}</td>
                                                                        <td>{item.confirmed_at ? moment(item.confirmed_at).format("Do MMM, YYYY HH:mm") : '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div id="activity" className="tab-pane fade">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mg-b-0">Activity</h6>
                                    </div>
                                    <div className="card-body">
                                        {activityLoading ? (
                                            <div className="d-flex align-items-center justify-content-center pd-y-20">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                                <span className="mg-l-10 tx-13">Loading...</span>
                                            </div>
                                        ) : activityLog.length === 0 ? (
                                            <p className="tx-color-03 mg-b-0">No activity for this user.</p>
                                        ) : (
                                            <ul className="activity tx-13">
                                                {activityLog.map((item) => (
                                                    <li key={item.id} className="activity-item">
                                                        <div className="activity-body">
                                                            <p className="mg-b-2">
                                                                <span className="tx-semibold">{item.event ?? '—'}</span>
                                                                {item.created_at && (
                                                                    <span className="tx-color-03 tx-12 mg-l-10">
                                                                        {moment(item.created_at).format("Do MMM, YYYY HH:mm")}
                                                                    </span>
                                                                )}
                                                            </p>
                                                            {item.message && <p className="mg-b-0 tx-color-03">{item.message}</p>}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetail;
